import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { getTenantFromUrl, isDevelopment, setDevTenantSubdomain } from '../utils/enhancedTenantUtils';

interface TenantLoaderProps {
  children: React.ReactNode;
}

const TenantLoader: React.FC<TenantLoaderProps> = ({ children }) => {
  const { isLoading, error, tenantData, isActive } = useTenant();
  const tenantInfo = getTenantFromUrl();

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading...</h2>
          <p className="text-gray-600">Preparing your workspace</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 text-red-500">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {error === 'Tenant not found' ? 'Workspace Not Found' : 
               error === 'Invalid subdomain' ? 'Invalid Workspace' :
               error === 'Tenant is inactive or suspended' ? 'Workspace Suspended' :
               'Connection Error'}
            </h2>
            <p className="text-gray-600 mb-6">
              {error === 'Tenant not found' ? 
                `The workspace "${tenantInfo.subdomain}" could not be found.` :
               error === 'Invalid subdomain' ?
                'The workspace URL is not valid.' :
               error === 'Tenant is inactive or suspended' ?
                'This workspace is currently inactive or suspended.' :
                'Unable to connect to your workspace.'}
            </p>
            
            {isDevelopment() && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">Development Mode</h3>
                <p className="text-sm text-yellow-700 mb-3">
                  You can switch to a different tenant for testing:
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDevTenantSubdomain('default')}
                    className="px-3 py-1 text-xs bg-yellow-200 text-yellow-800 rounded hover:bg-yellow-300"
                  >
                    Default
                  </button>
                  <button
                    onClick={() => setDevTenantSubdomain('demo')}
                    className="px-3 py-1 text-xs bg-yellow-200 text-yellow-800 rounded hover:bg-yellow-300"
                  >
                    Demo
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show inactive tenant message
  if (tenantData && !isActive) {
    const isTrialExpired = tenantData.status === 'trial' && new Date() > new Date();
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 text-orange-500">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {isTrialExpired ? 'Trial Expired' : 'Account Inactive'}
            </h2>
            <p className="text-gray-600 mb-6">
              {isTrialExpired 
                ? 'Your trial period has ended. Please contact support to continue using this workspace.'
                : 'This workspace is currently inactive. Please contact support for assistance.'}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Workspace: <span className="font-medium">{tenantData.name}</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => window.location.href = 'mailto:support@yourcompany.com'}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Contact Support
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render children if everything is good
  return <>{children}</>;
};

export default TenantLoader;